import React from 'react';
import { HashLink } from 'react-router-hash-link';
import { Page } from 'components/layout';
import { Flex, Container } from 'components/ui';
import styled from 'styled-components/macro';
import { Title } from 'components/ui/Title';

const Content = styled(Flex)`
  align-items: center;

  ${Title} {
    font-size: 35px;
  }

  section {
    margin-bottom: 10px;
    max-width: 800px;
    align-self: center;
  }

  h2 {
    margin-top: 40px;
  }

  h4 {
    font-size: 17px;
    margin-bottom: 8px;
  }

  h5 {
    font-size: 15px;
  }

  hr {
    width: 100%;
    max-width: 800px;
    margin-bottom: 20px;
    border-color: rgba(255, 255, 255, 0.3);
  }

  a {
    color: aquamarine;
  }

  th,
  td {
    border-bottom: 1px solid gray;
  }
`;



const AboutPage = () => {
  const videoWidth = window.innerWidth > 640 ? 640 : window.innerWidth - 60;

  const VideoWrap = styled.div`
    width: ${videoWidth}px;
    display: flex;
    flex-direction: column;
    align-items: center;

    p {
      font-size: smaller;
    }
  `;
  return (
    <Page title="About" fixedFooter={false}>
      <Container>
        <Content color="copy" column>
          <Title>About EulerBeats</Title>

          <section>
            <p>
              <HashLink to="#overview">Overview</HashLink>
              {' | '}
              <a
                href="https://eulerbeats.gitbook.io/eulerbeats-docs/genesis"
                target="_blank"
                rel="noreferrer"
              >
                Genesis
              </a>
              {' | '}
              <a
                href="https://eulerbeats.gitbook.io/eulerbeats-docs/enigma"
                target="_blank"
                rel="noreferrer"
              >
                Enigma
              </a>
              {' | '}
              <a
                href="https://eulerbeats.gitbook.io/eulerbeats-docs/in-the-news"
                target="_blank"
                rel="noreferrer"
              >
                In The News
              </a>
              {' | '}
              <a
                href="https://eulerbeats.gitbook.io/eulerbeats-docs/faq"
                target="_blank"
                rel="noreferrer"
              >
                FAQ
              </a>
              {' | '}
              <HashLink to="#story">EulerBeats Story</HashLink>
              {' | '}
              <a
                href="https://eulerbeats.gitbook.io/eulerbeats-docs/project-updates"
                target="_blank"
                rel="noreferrer"
              >
                Project Updates
              </a>
            </p>
          </section>

          <section>
            <p>
              Imagine music as mathematical art on an&nbsp;
              <a
                href="https://decrypt.co/resources/non-fungible-tokens-nfts-explained-guide-learn-blockchain"
                target="_blank"
                rel="noreferrer"
              >
                NFT
              </a>
              &nbsp;substrate. This is EulerBeats. The Euler Record is a limited edition set of
              algorithmically generated art + music based on the Euler number and the Euler's totient
              function. Genesis (27 NFTs) and Enigma (27 NFTs) are two sides of the Euler Record.
              The EulerBeats smart contract extends the ERC-1155 Ethereum multi-token standard to
              create non-fungible unique original tokens and a set of fungible print tokens for each
              original. Both releases share a common mathematical basis, but differ in art, music,
              and bonding curve structures. Each original NFT has a limited set of prints priced on
              a bonding curve.
            </p>

            <p>
              The token is the art piece in itself. Everything needed to re-create each masterpiece
              is stored on-chain on Ethereum. EulerBeats is a careful and creative ensemble of math,
              art, music, royalties, instantly liquid tokens, and scarcity into a neat digital pack.
              It’s a first of its kind.
            </p>
          </section>

          <hr />
          <Title id="overview">Overview</Title>
          <section>
            <h3>Diamonds are forever. EulerBeats are for eternity.</h3>
            <p>
              This art is designed to last a lifetime. The data and the script to re-generate the
              art and music are stored on the Ethereum blockchain. If the EulerBeats website is ever
              shut down, you can use the seed and the script to generate your art and the beat. We
              show how in a video in the FAQ section. This project joined the ranks of a handful of
              pioneering NFT projects that keep all data and logic required to construct the art and
              music permanently on Ethereum for eternity. The Phosphor team pushed the boundaries of
              the “on-chain” NFTs storage by placing the “recipe” for art, music, and economics into
              an Ethereum block. This is in contrast to many NFT projects that store the token media
              only on IPFS-like decentralized storage, or even in centralized cloud storage. Our
              article &nbsp;
              <a
                href="https://medium.com/treum_io/on-chain-artwork-nfts-f0556653c9f3"
                target="_blank"
                rel="noopener noreferrer"
              >
                On-Chain Artwork NFTs
              </a>
              &nbsp; provides an elegant explanation of the differences in media storage choices
              between different NFT projects.
            </p>
          </section>

          <section>
            <h3>Originals</h3>
            <p>
              Originals of the Euler Record are ultra scarce NFTs and are capped at 27 for both
              Enigma and Genesis. Originals birth a new art + beat based on the Euler's totient
              function. The token seed is generated on-chain. Nobody knows the look and sound of the
              token until it’s minted. The music and the artistic design of Genesis and Enigma are
              unique but they feel familiar.
            </p>
          </section>

          <section>
            <h3>Owning Originals</h3>
            <p>
              Buyers of Originals shouldn’t need any more incentive other than the historical
              significance and the uniqueness of the art + music EulerBeats NFTs. That said, all
              EulerBeats Original holders will receive 8% of the print price on each print sold of
              the original token they own. The royalties are paid automatically as they are
              programmed into the smart contract and cannot be changed. In addition to the
              royalties, holders of Originals have full commercial rights. Some Genesis original
              owners have already created remixes and collaborations with other projects.
            </p>
          </section>

          <section>
            <h3>Prints</h3>
            <p>
              EulerBeats prints are replicas of an Original and can be previewed by the buyer before
              purchase. The cap for the fungible print tokens is set to 119 for each Genesis
              original token and 160 for each Enigma original token.
            </p>
            <p>
              Any collector can buy a print based on the price formula set on a bonding curve. As
              the number of prints in circulation grows for a particular original, the price of
              issuing its next print increases at an exponential rate.
            </p>
          </section>

          <section>
            <h3>Reserve</h3>
            <p>
              A percentage of the print price gets instantly placed into a reserve in the smart
              contract. The reserve provides instant liquidity to a print holder who decides to
              “burn” a print. Genesis and Enigma have separate reserve pools. The reserve is not
              accessible by the project team and it’s only accessible by the smart contract to pay
              print holders when they burn their prints.
            </p>
          </section>

          <section>
            <h3>Burning</h3>
            <p>
              The bonding curve acts as a liquid market that allows a print holder to burn their
              print token, thus reducing the current print token supply of the associated Original,
              in exchange for receiving the burn price at that time. Thus, the burning process
              returns ETH to the print token burner from the reserve. The burn price is determined
              according to the formula set on the bonding curve. Genesis and Enigma have different
              burn price calculations (see below). It’s not possible to burn a print into an abyss
              by sending it to a 0x000… address. We are using an OpenZeppelin base contract, which
              does not allow ERC-1155 transfers to the zero address.
            </p>
          </section>

          <section id="bonding-curves">
            <h3>Bonding Curves</h3>
            <p>
              Both Genesis and Enigma use different bonding curves to calculate the print and burn
              prices. The print purchase price and the burn price paid back upon the burn of a print
              token are set on the exponential bonding curve determined by the current token supply
              for a given Original. The higher the token supply the higher the print and burn price,
              and vice versa. The EulerBeats bonding curves for Genesis and Enigma are transparent
              and not changeable since they are programmed into a smart contract on Ethereum
              blockchain. Additional details are below.
            </p>
          </section>

          <section id="deepdive">
            <h3>Deep Dive</h3>
            <p>
              Read more about the design goals and differences between Genesis and Enigma &nbsp;
              <a
                href="https://eulerbeats.gitbook.io/eulerbeats-docs/"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              . We cover everything from the different bonding curves, art and music generation traits, and design considerations. We also have information about print staking, command line interface tool for developers, and information about our grants program.
            </p>
          </section>

         
        <hr/>

          

          <Title id="story">EulerBeats Story</Title>

          <section>
            <p>
              <a href="https://phosphor.xyz" target="_blank" rel="noopener noreferrer">
                phosphor.xyz
              </a>
              , a ConsenSys backed company incorporated in 2016, is one of the early leaders in the
              blockchain space. Phosphor team has a demonstrable track record for launching some epic
              firsts in blockchain including but not limited to implementing the first non-financial
              use case for tracking oil samples for BHP Billiton, Bait-to-plate tracking of
              yellowfin Tuna from Fiji to NY with WWF, live auction of tokenized Game worn Jerseys
              for Sacramento Kings. Our most recent project,{' '}
              <a href="https://swether.io" target="_blank" rel="noopener noreferrer">
                Swether.io
              </a>
              , which ran through December 2020, enabled crypto users to design, mint, and gift a
              swether token NFT and receive a sweatshirt with their very own custom design. After
              the Swether project, we continued to enhance the Phosphor tokenization platform to
              include a market price discovery mechanism using a{' '}
              <a
                href="https://coinmarketcap.com/alexandria/glossary/bonding-curve"
                target="_blank"
                rel="noopener noreferrer"
              >
                bonding curve
              </a>
              .
            </p>

            <p>
              For the first project we decided to build a bonding curve based on the{' '}
              <a
                href="https://en.wikipedia.org/wiki/E_(mathematical_constant)"
                target="_blank"
                rel="noopener noreferrer"
              >
                Euler's number
              </a>{' '}
              (mathematical constant) and the totient function(ɸ) to celebrate the mathematician's
              work. From Calculus textbooks to compound interest to cryptography: you have
              experienced Euler’s work in theory and practice. We launched EulerBeats to commemorate{' '}
              <a
                href="https://en.wikipedia.org/wiki/Leonhard_Euler"
                target="_blank"
                rel="noopener noreferrer"
              >
                Leonhard Euler
              </a>
              's discovery of his{' '}
              <a
                href="https://en.wikipedia.org/wiki/Euler's_totient_function#History,_terminology,_and_notation"
                target="_blank"
                rel="noopener noreferrer"
              >
                totient function phi (ɸ)
              </a>
              .&nbsp; Leonhard Euler’s contributions to mathematics are well known and include
              geometry, infinitesimal calculus, trigonometry, algebra, and number theory, as well as
              continuum physics, lunar theory and other areas of physics [
              <a
                href="https://en.wikipedia.org/wiki/Leonhard_Euler#Contributions_to_mathematics_and_physics"
                target="_blank"
                rel="noopener noreferrer"
              >
                Wikipedia
              </a>
              ].
            </p>

            <p>
              Euler’s{' '}
              <a
                href="https://en.wikipedia.org/wiki/Leonhard_Euler#Music"
                target="_blank"
                rel="noopener noreferrer"
              >
                musical mathematics
              </a>{' '}
              reflected his passion for sound. He looked to find and explain harmony in music with
              math. He believed it’s possible to quantify the “degree of agreeableness which might
              be translated as sweetness, charm, or tunefulness.” The relationship between math,
              physics, music and art is tightly intertwined. Fittingly we packaged math, art, music,
              royalties, instant liquidity, and scarcity into an NFT project dubbed as EulerBeats.
            </p>
          </section>

          <section>
            <h3>Collaborating with EulerBeats</h3>
            <p>
              Phosphor is an NFT platform. We will continue to work with artists, creators, and
              production houses entering the token economy space to accelerate their way to token
              creation, pricing and price discovery, and royalty distribution. Please{' '}
              <a href="https://eulerbeats.com/contact" target="_blank" rel="noopener noreferrer">
                reach out
              </a>{' '}
              to us if you would like to explore a collaboration. Follow this project on Twitter{' '}
              <a href="https://twitter.com/eulerbeats" target="_blank" rel="noopener noreferrer">
                @eulerbeats
              </a>{' '}
              and the team{' '}
              <a href="https://twitter.com/treum_io" target="_blank" rel="noopener noreferrer">
                @treum_io
              </a>{' '}
              to chat with the creators and collectors.
            </p>

            
          </section>

          <VideoWrap>
            <iframe
              title="Preview"
              src="https://player.vimeo.com/video/529293546"
              width={videoWidth}
              height={videoWidth / 1.7}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
            <p>
              <a href="https://vimeo.com/529293546">EulerBeats Enigma Preview</a> from{' '}
              <a href="https://vimeo.com/user82855564">Phosphor</a> on{' '}
              <a href="https://vimeo.com">Vimeo</a>.
            </p>
          </VideoWrap>

          <VideoWrap>
            <iframe
              title="Preview"
              src="https://player.vimeo.com/video/510490782"
              width={videoWidth}
              height={videoWidth / 1.7}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
            <p>
              <a href="https://vimeo.com/510490782">EulerBeats Genesis Preview</a> from{' '}
              <a href="https://vimeo.com/user82855564">Phosphor</a> on{' '}
              <a href="https://vimeo.com">Vimeo</a>.
            </p>
          </VideoWrap>

          
         

          

        </Content>
      </Container>
    </Page>
  );
};

export default AboutPage;
